import React from 'react';
import { Box, Tooltip, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ShoppingCartCheckoutOutlined as DirectDropIcon,
  AttachMoney as UpdatePricesIcon,
  Description as CopyFTListIcon,
  Image as CopyFTImageListIcon,
  ShoppingCart as AddToCartIcon,
  RemoveShoppingCart as RemoveFromCartIcon,
  Delete as MoveToTrashIcon,
} from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import toast from 'react-hot-toast';
import ToolsIcon from '../ToolsIcon';

export interface BulkActionsProps {
  selectedItems: number[];
  selectedItemsData?: Array<{
    id: number;
    name: string;
    fg_price?: string | number | null;
    fiat_price?: string | number | null;
  }>;
  currency?: 'fg' | 'fiat';
  size?: 'small' | 'medium' | 'large';
  showToolsIcon?: boolean;
  onDirectDrop?: () => void;
  onUpdatePrices?: () => void;
  onCopyFTList?: () => void;
  onCopyFTImageList?: () => void;
  onAddToCart?: () => void;
  onRemoveFromCart?: () => void;
  onMoveToTrash?: () => void;
  sx?: any;
}

export default function BulkActions({
  selectedItems,
  selectedItemsData,
  currency = 'fg',
  size = 'small',
  showToolsIcon = true,
  onDirectDrop,
  onUpdatePrices,
  onCopyFTList,
  onCopyFTImageList,
  onAddToCart,
  onRemoveFromCart,
  onMoveToTrash,
  sx = {},
}: BulkActionsProps) {
  const { t } = useTranslation();

  const isDisabled = selectedItems.length === 0;

  // Calculate total value
  const calculateTotalValue = () => {
    if (!selectedItemsData || selectedItemsData.length === 0) {
      return 0;
    }

    return selectedItemsData.reduce((total, item) => {
      const priceStr = currency === 'fg' ? item.fg_price : item.fiat_price;
      const price = parseFloat(priceStr as string);

      // Check for invalid prices like the existing code does
      if (isNaN(price) || priceStr === null || priceStr === undefined || priceStr === '') {
        return total; // Don't add invalid prices
      }

      return total + price;
    }, 0);
  };

  const totalValue = calculateTotalValue();
  const currencySymbol = currency === 'fg' ? 'fg' : '$';

  // Format selected items display
  const formatSelectedItems = () => {
    if (!selectedItemsData || selectedItemsData.length === 0) {
      return `${selectedItems.length} ${t('common.selected')}`;
    }

    // Group items by name and count quantities
    const itemCounts = selectedItemsData.reduce(
      (acc, item) => {
        acc[item.name] = (acc[item.name] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    // Create formatted item names with quantities
    const formattedItems = Object.entries(itemCounts).map(([name, count]) => {
      return count > 1 ? `${count}x ${name}` : name;
    });

    const itemsText =
      formattedItems.length === 1
        ? formattedItems[0]
        : formattedItems.length === 2
          ? `${formattedItems[0]}, ${formattedItems[1]}`
          : formattedItems.length <= 3
            ? formattedItems.join(', ')
            : `${formattedItems.slice(0, 2).join(', ')}, and ${formattedItems.length - 2} more`;

    // Show value if items have pricing
    if (totalValue > 0) {
      return `${itemsText} vs ${totalValue.toFixed(2)} ${currencySymbol}`;
    }

    return itemsText;
  };

  // Format all selected items for tooltip
  const formatAllSelectedItems = () => {
    if (!selectedItemsData || selectedItemsData.length === 0) {
      return `${selectedItems.length} ${t('common.selected')}`;
    }

    // Group items by name and count quantities
    const itemCounts = selectedItemsData.reduce(
      (acc, item) => {
        acc[item.name] = (acc[item.name] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    // Create formatted item names with quantities, each on a new line
    const formattedItems = Object.entries(itemCounts).map(([name, count]) => {
      return count > 1 ? `${count}x ${name}` : name;
    });

    const itemsList = formattedItems.join('\n');

    // Show total value if items have pricing
    if (totalValue > 0) {
      return `${itemsList}\n\n${totalValue.toFixed(2)} ${currencySymbol}`;
    }

    return itemsList;
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = () => {
    const textToCopy = formatAllSelectedItems();
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        toast.success(t('common.copiedToClipboard'));
      })
      .catch(() => {
        toast.error(t('common.clipboardError'));
      });
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: showToolsIcon ? 'space-between' : 'center',
        gap: showToolsIcon ? '8px' : '4px',
        padding: '4px',
        borderRadius: '4px',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: (theme) => theme.palette.background.paper,
        marginBottom: '8px',
        ...sx,
      }}
    >
      {/* Tools Icon with Selected Items */}
      {showToolsIcon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <ToolsIcon />
          {selectedItems.length > 0 && (
            <Tooltip
              title={
                <Box sx={{ position: 'relative' }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-line' }}>
                    {formatAllSelectedItems()}
                  </pre>
                  <IconButton
                    onClick={handleCopyToClipboard}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            >
              <Typography
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  fontWeight: 500,
                  minWidth: 'fit-content',
                }}
              >
                {formatSelectedItems()}
              </Typography>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Actions Container */}
      <Box
        sx={{
          display: 'flex',
          gap: '4px',
          flex: showToolsIcon ? 1 : 'none',
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        {/* Direct Drop Action */}
        {onDirectDrop && (
          <Tooltip title={t('actions.directDrop')}>
            <IconButton
              disabled={isDisabled}
              size={size}
              sx={{
                color: (theme) => theme.palette.warning.main,
                backgroundColor: (theme) => `${theme.palette.warning.main}15`,
                border: (theme) => `1px solid ${theme.palette.warning.main}60`,
                '&:hover': {
                  backgroundColor: (theme) => `${theme.palette.warning.main}25`,
                  border: (theme) => `1px solid ${theme.palette.warning.main}`,
                },
                '&:disabled': {
                  color: (theme) => theme.palette.action.disabled,
                  backgroundColor: 'transparent',
                  border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                },
              }}
              onClick={onDirectDrop}
            >
              <DirectDropIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Add to Cart Action */}
        {onAddToCart && (
          <Tooltip title={t('search.cart.addToCart')}>
            <IconButton
              disabled={isDisabled}
              size={size}
              sx={{
                color: (theme) => theme.palette.warning.main,
                border: (theme) => `1px solid ${theme.palette.warning.main}60`,
                '&:hover': {
                  border: (theme) => `1px solid ${theme.palette.warning.main}`,
                },
                '&:disabled': {
                  color: (theme) => theme.palette.action.disabled,
                  border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                },
              }}
              onClick={onAddToCart}
            >
              <AddToCartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Remove from Cart Action */}
        {onRemoveFromCart && (
          <Tooltip title={t('search.cart.removeFromCart')}>
            <IconButton
              disabled={isDisabled}
              size={size}
              sx={{
                color: (theme) => theme.palette.warning.main,
                border: (theme) => `1px solid ${theme.palette.warning.main}60`,
                '&:hover': {
                  border: (theme) => `1px solid ${theme.palette.warning.main}`,
                },
                '&:disabled': {
                  color: (theme) => theme.palette.action.disabled,
                  border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                },
              }}
              onClick={onRemoveFromCart}
            >
              <RemoveFromCartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Update Prices Action */}
        {onUpdatePrices && (
          <Tooltip title={t('search.cart.bulkUpdate')}>
            <IconButton
              disabled={isDisabled}
              size={size}
              sx={{
                color: (theme) => theme.palette.text.secondary,
                border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                '&:hover': {
                  color: (theme) => theme.palette.text.primary,
                  border: (theme) => `1px solid ${theme.palette.secondary.main}`,
                },
              }}
              onClick={onUpdatePrices}
            >
              <UpdatePricesIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Copy FT List Action */}
        {onCopyFTList && (
          <Tooltip title="Copy FT Text List">
            <IconButton
              disabled={isDisabled}
              size={size}
              sx={{
                color: (theme) => theme.palette.text.secondary,
                border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                '&:hover': {
                  color: (theme) => theme.palette.text.primary,
                  border: (theme) => `1px solid ${theme.palette.secondary.main}`,
                },
              }}
              onClick={onCopyFTList}
            >
              <CopyFTListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Copy FT Image List Action */}
        {onCopyFTImageList && (
          <Tooltip title="Copy FT Image List">
            <IconButton
              disabled={isDisabled}
              size={size}
              sx={{
                color: (theme) => theme.palette.text.secondary,
                border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                '&:hover': {
                  color: (theme) => theme.palette.text.primary,
                  border: (theme) => `1px solid ${theme.palette.secondary.main}`,
                },
              }}
              onClick={onCopyFTImageList}
            >
              <CopyFTImageListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Move to Trash Action */}
        {onMoveToTrash && (
          <Tooltip title={t('actions.moveToTrash')}>
            <IconButton
              disabled={isDisabled}
              size={size}
              sx={{
                color: (theme) => theme.palette.text.secondary,
                border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                '&:hover': {
                  color: (theme) => theme.palette.text.primary,
                  border: (theme) => `1px solid ${theme.palette.secondary.main}`,
                },
              }}
              onClick={onMoveToTrash}
            >
              <MoveToTrashIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Spacer for centering when ToolsIcon is shown */}
      {showToolsIcon && <Box sx={{ width: '32px' }} />}
    </Box>
  );
}
